import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
 
const DollarSignIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 0 0 7v-4"></path>
    <path d="M19 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 0 0 7v-4"></path>
  </svg>
);
 
// New maximize icon
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
 
// New minimize icon
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
 
const API_URL = '/api';
 
const CurrencyViewModal = ({ open, onClose, currency }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);
 
  // ✅ Department feature: screen-config state + helpers
  const [screenConfig, setScreenConfig] = useState(null);
  const SCREEN_NAME = 'CurrencyMaster';
 
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };
 
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };
 
  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
 
      if (!res.ok) {
        let body = null;
        try { body = await res.json(); } catch (e) {}
        const msg = body?.message ? body.message : `Server returned ${res.status}`;
        throw new Error(msg);
      }
 
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      console.log('Screen info.....', cfg);
      setScreenConfig(cfg);
      return cfg;
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
      return null;
    }
  };
 
  useEffect(() => {
    if (open && currency) {
      fetchScreenConfig();
    }
  }, [open, currency]);
 
  if (!open || !currency) return null;
 
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('YYYY-MM-DD');
  };
 
  const getStatusColor = (status) => {
    // Handle boolean, string, and numeric status values
    const isActive = status === true || status === 'Active' || status === 1 || status === '1';
    return isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white';
  };
 
  const getStatusText = (status) => {
    // Convert boolean, numeric or string status to text
    if (status === true || status === 1 || status === '1') return 'Active';
    if (status === false || status === 0 || status === '0') return 'Inactive';
    return status || 'Unknown';
  };
 
  // Define sections array similar to VendorViewModal
  const sections = [
    {
      title: 'Currency Information',
      fields: [
        { label: 'Currency Code', value: currency.CurrencyCode },
        { label: 'Currency Name', value: currency.CurrencyName },
        { label: 'Symbol', value: currency.Symbol },
        { label: 'Default Rate', value: currency.DefaultRate },
 
        // ✅ Department (single field: Name ID Code inline) — header-level
        ...(shouldShowDepartmentOnHeader(screenConfig || currency?.screenConfig)
          ? [{
              label: 'Department',
              value: ` ${currency.DepartmentCode || '-'}`,
            }]
          : []),
 
        {
          label: 'Status',
          value: (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(currency.Status)}`}>
              {getStatusText(currency.Status)}
            </span>
          )
        },
        { label: 'Effective Date', value: formatDate(currency.EffectiveDate) }
      ],
      name: 'Currency Info'
    },
    {
      title: 'System Information',
      fields: [
        {
          label: 'Last Updated',
          value: currency.LastUpdated ? dayjs(currency.LastUpdated).format('YYYY-MM-DD HH:mm') : '-'
        },
        { label: 'Created By', value: currency.CreatedBy || '-' }
      ],
      name: 'System Info'
    }
  ];
 
  // Navigation functions (kept as-is)
  const handleNext = () => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
 
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
 
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
 
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* Internet Explorer 10+ */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
 
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
 
      {/* Panel */}
      <div
        className={`bg-white shadow-2xl h-full flex flex-col
          transition-transform transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full max-w-full' : 'w-full max-w-6xl'}`}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#6A89A7] rounded-full flex items-center justify-center">
                <DollarSignIcon className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#384959]">Currency Details</h2>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]"
                onClick={toggleMaximize}
                title={isMaximized ? 'Minimize' : 'Maximize'}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              <button
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]"
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
 
        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto scrollbar px-8 pb-6 space-y-6">
          {/* Render all sections */}
          {sections.map((section, index) => (
            <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">{section.title}</h3>
              <div className="grid grid-cols-1 gap-4">
                {section.fields.map((field, fieldIndex) => (
                  <div key={fieldIndex} className="flex flex-col">
                    <span className="text-sm font-medium text-[#64748b]">{field.label}</span>
                    <span className="text-base text-[#384959] mt-1 font-semibold break-words uppercase">
                      {field.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
 
          {/* Currency Summary */}
          <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
            <h3 className="text-lg font-semibold text-[#384959] mb-4">Currency Summary</h3>
            <p className="text-[#64748b] leading-relaxed">
              This currency is configured with the symbol{' '}
              <span className="font-semibold text-[#384959]">{currency.Symbol}</span> and has a default
              exchange rate of{' '}
              <span className="font-semibold text-[#384959]">{currency.DefaultRate}</span>. The currency
              is currently{' '}
              <span
                className={`inline-block px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(
                  currency.Status
                )}`}
              >
                {getStatusText(currency.Status)}
              </span>{' '}
              and has been effective since{' '}
              <span className="font-semibold text-[#384959]">{formatDate(currency.EffectiveDate)}</span>.
            </p>
          </div>
        </div>
 
        {/* Footer */}
        <div className="flex-shrink-0 p-8 pt-0 flex justify-end gap-3">
          <button
            className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
 
export default CurrencyViewModal;
 